import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useRegistration } from '../Registration';
import { registerUserFromWebsite } from '../../../firebase/authService';
import { useLanguage } from '../../../contexts/LanguageContext';

const Step6Terms = () => {
  const { formData, prevStep, navigate } = useRegistration();
  const { t } = useLanguage();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const copy = t('registration.steps.terms', {});

  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: { privacyAgreement: formData.privacyAgreement }
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    setError('');

    try {
      // Combine all form data
      const completeUserData = {
        ...formData,
        ...data
      };

      const result = await registerUserFromWebsite(completeUserData);

      if (result.success) {
        setSuccess(true);
        // Redirect to success page after a delay
        setTimeout(() => {
          navigate('/auth/success');
        }, 3000);
      } else {
        setError(copy.errors?.generic || t('common.error'));
      }
    } catch (err) {
      console.error('Registration error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError(copy.errors?.emailInUse);
      } else if (err.code === 'auth/weak-password') {
        setError(copy.errors?.weakPassword);
      } else {
        setError(copy.errors?.unexpected || t('common.error'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="text-center space-y-6">
        <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {copy.success?.title}
          </h2>
          <p className="text-gray-600 mb-6">
            {copy.success?.subtitle}
          </p>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-left">
          <h3 className="font-semibold text-blue-900 mb-2">{copy.success?.nextTitle}</h3>
          <ul className="text-sm text-blue-800 space-y-1">
            {Array.isArray(copy.success?.nextSteps) && copy.success.nextSteps.map((item) => (
              <li key={item}>• {item}</li>
            ))}
          </ul>
        </div>

        <div className="space-y-3">
          <button
            onClick={() => navigate('/auth/login')}
            className="w-full bg-blue-600 text-white py-3 px-6 rounded-lg font-medium hover:bg-blue-700 transition-all duration-200"
          >
            {copy.success?.signIn}
          </button>
          
          <button
            onClick={() => navigate('/')}
            className="w-full bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200"
          >
            {copy.success?.home}
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
          {copy.title}
        </h2>
        <p className="text-gray-600 text-center mb-8">
          {copy.description}
        </p>
      </div>

      {/* Terms and Privacy Content */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-4 max-h-64 overflow-y-auto">
        {Array.isArray(copy.sections) && copy.sections.map((section) => (
          <div key={section.title}>
            <h3 className="font-semibold text-gray-900 mb-2">{section.title}</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              {section.description}
            </p>
          </div>
        ))}
      </div>

      {/* Privacy Agreement Checkbox */}
      <div className="flex items-start">
        <input
          {...register('privacyAgreement', { 
            required: copy.checkbox?.error
          })}
          type="checkbox"
          id="privacyAgreement"
          className="mt-1 h-4 w-4 text-prayer-red focus:ring-prayer-red border-gray-300 rounded"
        />
        <label htmlFor="privacyAgreement" className="ml-3 text-sm text-gray-700">
          {copy.checkbox?.label}
        </label>
      </div>
      {errors.privacyAgreement && (
        <p className="text-sm text-red-600">{errors.privacyAgreement.message}</p>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6">
        <div className="flex space-x-3">
          <button
            type="button"
            onClick={() => navigate('/')}
            disabled={isLoading}
            className="px-4 py-3 text-gray-600 hover:text-gray-800 font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {copy.navigation?.home}
          </button>
          <button
            type="button"
            onClick={prevStep}
            disabled={isLoading}
            className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('registration.previous')}
          </button>
        </div>
        
        <button
          type="submit"
          disabled={isLoading}
          className="px-6 py-3 bg-prayer-red text-white rounded-lg font-medium hover:bg-prayer-red-light transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {isLoading ? (
            <>
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Creating Account...</span>
            </>
          ) : (
            <span>{t('registration.completeRegistration')}</span>
          )}
        </button>
      </div>
    </form>
  );
};

export default Step6Terms;
